import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { debounceTime } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

export interface IDateRange {
  startDate: Date;
  endDate: Date;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit, OnChanges, AfterViewInit {
  range = new FormGroup({
    //start date for date range
    start: new FormControl(),
    //end date for date range
    end: new FormControl()
  });

  // selected start date.
  @Input() userSelectedStartDate!: Date | null;

  // selected end date.
  @Input() userSelectedEndDate!: Date | null;

  // whether the calendar is opend or not
  @Input() isOpened = false;

  // whether to reset the existing date selection or not
  @Input() shouldResetDateRange = false;

  // whether to set date with default value or not
  @Input() shouldSetDefaultDate = true;

  // Emits when currently selected date changes.
  @Output() selectedStartDate: EventEmitter<Date | null> =
    new EventEmitter<Date | null>();

  // Emits when currently selected date changes.
  @Output() selectedEndDate: EventEmitter<Date | null> = new EventEmitter<Date | null>();

  // emits when currenly selected date range changes
  @Output() selectedDateRangeChange: EventEmitter<IDateRange> =
    new EventEmitter<IDateRange>();

  // to acess the date range picker
  @ViewChild('picker') datePicker!: MatDateRangePicker<Date>;

  //emits when date range is close
  @Output() closeDatepickerDialog: EventEmitter<void> = new EventEmitter();

  constructor(
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly translate: TranslateService
  ) {
    //this.dateAdapter.setLocale(this.translate.instant('datepicker.languageCode'));
  }

  ngOnInit(): void {
    this.initiateData();
    this.resetDateSelection();
  }

  // Will run everytime @Input changes
  // Will need this to open the calendar popup
  ngOnChanges(): void {
    this.isOpened && this.datePicker.open();
    this.initiateData();
    this.resetDateSelection();
  }

  ngAfterViewInit(): void {
    // Emits the current date range once when any or both of start end end changes.
    this.range.valueChanges.pipe(debounceTime(100)).subscribe((event) => {
      const date: IDateRange = {
        startDate: event.start,
        endDate: event.end || event.start
      };

      // If end date is null, then set start date to end date
      event.end == null
        ? this.range.patchValue({
            end: date.endDate
          })
        : this.selectedDateRangeChange.emit(date);
    });
  }

  /**
   * This method emits close daterange dialog
   */
  daterangeClose = () => this.closeDatepickerDialog.emit();

  /**
   * Resets selected date range.
   */
  resetDateSelection = () => {
    if (this.shouldResetDateRange) {
      this.range.reset();
      this.shouldResetDateRange = false;
    }
  };

  /**
   * Function emits start date
   *
   * @param {Date} startDate
   */
  dateSelectedStart = (startDate: Date) => {
    this.selectedStartDate.emit(startDate);
    this.userSelectedStartDate = startDate;
  };

  /**
   * Function emits end date
   *
   * @param {Date|null} endDate
   *
   */
  dateSelectedEnd = (endDate: Date | null) => {
    if (endDate) {
      this.selectedEndDate.emit(endDate);
      this.userSelectedEndDate = endDate;
    } else {
      this.selectedEndDate.emit(this.userSelectedStartDate);
      this.userSelectedEndDate = this.userSelectedStartDate;
    }
  };

  /**
   * This method will help to close the calendar
   */
  closeCalendar = () => (this.isOpened = false);

  /**
   * This method will initialize the start and end dates
   */
  initiateData = () => {
    if (this.shouldSetDefaultDate) {
      this.range.setValue({
        start: this.getTomorrowsDate(),
        end: this.getTomorrowsDate()
      });
      this.shouldSetDefaultDate = false;
    }
  };

  /**
   * This will help to get the tomorrow's date
   *
   * @returns {Date} tomorrow
   */
  getTomorrowsDate = () => {
    const dateToday = new Date(),
      dateTomorrow = new Date(dateToday.setDate(dateToday.getDate() + 1));
    return dateTomorrow;
  };
}
